import { FileImageIcon } from "@phosphor-icons/react/dist/csr/FileImage";
import { useEffect, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { Attachment } from "./enrich-attachments";
import { useAttachmentImage } from "./use-attachment-image";

export const AttachmentImage = ({
  file,
  expanded = false,
}: {
  file: Attachment;
  expanded?: boolean;
}) => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(expanded);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!node || visible) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, visible]);
  const { src, error, retry } = useAttachmentImage(file, visible);
  return (
    <Stack
      ref={setNode}
      align="center"
      justify="center"
      className={cn("min-h-16 w-full overflow-hidden bg-surface-sunken", {
        "h-16": !expanded,
      })}
    >
      {failed || error ? (
        <Stack gap={1} align="center" className="p-2 text-xs text-subtle">
          <FileImageIcon aria-hidden className="size-6" />
          <span>Preview unavailable</span>
          {expanded && (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => {
                setFailed(false);
                retry();
              }}
            >
              Retry preview
            </Button>
          )}
        </Stack>
      ) : src ? (
        <img
          src={src}
          alt={expanded ? file.name : ""}
          onError={() => setFailed(true)}
          className={cn("w-full object-contain", {
            "h-16": !expanded,
            "max-h-[70dvh]": expanded,
          })}
        />
      ) : (
        <Skeleton
          className={cn("w-full", { "h-16": !expanded, "h-64": expanded })}
        />
      )}
    </Stack>
  );
};
